// HeaderAccountPanel - Unified panel for credits, supporter badge, max badge, and account avatar
// Replaces the separate credits bar, max badge, supporter badge, and account menu container

import { useState, useRef, useEffect } from 'react';
import { RevoltTooltip } from './RevoltTooltip';

const SUPPORTER_TOOLTIP_TEXT =
  'Financially supporting Revolt in any way (even one-time) gives you this badge and access to the Community tab.';

const MAX_TOOLTIP_TEXT =
  'You have the Max plan with premium credits and features.';

const BETA_TOOLTIP_TEXT =
  'You are a Beta Tester. Thanks for helping us validate and improve new features.';

/** Mobile row 1 right: badges + avatar (no pill container) */
export function HeaderAccountPanelMobileTop({
  user,
  isMaxPlan,
  isSupporter,
  isBetaTester,
  storedAccounts,
  onAccountMenuClick,
  onAdminClick,
  onLogout,
  onAccountSwitch,
  onRemoveAccount,
}) {
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAccountDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAccountButtonClick = () => setAccountDropdownOpen(!accountDropdownOpen);
  const handleAccountMenuClick = async () => { setAccountDropdownOpen(false); await onAccountMenuClick(); };
  const handleAdminClick = async () => { setAccountDropdownOpen(false); await onAdminClick(); };
  const handleLogout = async () => { setAccountDropdownOpen(false); await onLogout(); };
  const handleAccountSwitch = async (accountId) => {
    setAccountDropdownOpen(false);
    try {
      const result = await onAccountSwitch(accountId);
      if (!result?.success) console.error('Failed to switch account:', result?.error);
    } catch (error) {
      console.error('Account switch error:', error);
    }
  };

  return (
    <div className="header-account-mobile-top" ref={dropdownRef}>
      <div className="header-account-mobile-top__inner">
        {isSupporter && (
          <HeaderPanelBadge
            icon="fa-solid fa-check"
            className="header-account-panel__badge header-account-panel__badge--supporter"
            tooltipTitle="Supporter"
            tooltipText={SUPPORTER_TOOLTIP_TEXT}
          />
        )}
        {isMaxPlan && (
          <HeaderPanelBadge
            icon="fa-solid fa-crown"
            className="header-account-panel__badge header-account-panel__badge--max"
            tooltipTitle="Max User"
            tooltipText={MAX_TOOLTIP_TEXT}
          />
        )}
        {isBetaTester && (
          <HeaderPanelBadge
            icon="fa-solid fa-flask"
            className="header-account-panel__badge header-account-panel__badge--beta"
            tooltipTitle="Beta Tester"
            tooltipText={BETA_TOOLTIP_TEXT}
          />
        )}
        <div
          className="header-account-panel__avatar"
          title="Account Menu"
          onClick={handleAccountButtonClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleAccountButtonClick()}
          aria-haspopup="menu"
          aria-expanded={accountDropdownOpen}
        >
          {user?.initials ? <span className="user-initial">{user.initials}</span> : <i className="fa-solid fa-user" />}
        </div>
      </div>
      {accountDropdownOpen && (
        <div className="account-dropdown header-account-panel__dropdown">
          {storedAccounts
            .sort((a, b) => {
              const aIsCurrent = user?.email === a.email;
              const bIsCurrent = user?.email === b.email;
              if (aIsCurrent && !bIsCurrent) return -1;
              if (!aIsCurrent && bIsCurrent) return 1;
              return 0;
            })
            .map((account) => {
              const isCurrentAccount = user?.email === account.email;
              return (
                <a
                  key={account.id}
                  href={`/auth?switchToAccount=${account.id}&redirectTo=${encodeURIComponent(typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/')}`}
                  className={`account-dropdown-item account-full-item ${isCurrentAccount ? 'current' : ''}`}
                  data-navigation-button
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isCurrentAccount) handleAccountSwitch(account.id);
                  }}
                >
                  <div className="account-full-info">
                    <div className="account-avatar">
                      {account.userData?.initials || account.displayName?.charAt(0)?.toUpperCase() || account.email?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="account-details">
                      <div className="account-name">{account.displayName}</div>
                      <div className="account-email">{account.email}</div>
                    </div>
                  </div>
                  <div className="account-actions">
                    {isCurrentAccount && <i className="fa-solid fa-check account-check" />}
                    {!isCurrentAccount && (
                      <button type="button" className="account-remove-btn" onClick={(e) => onRemoveAccount(account.id, e)} title="Remove account">
                        <i className="fa-solid fa-times" />
                      </button>
                    )}
                  </div>
                </a>
              );
            })}
          <div className="account-dropdown-divider" />
          <a href="/account" className="account-dropdown-item" data-navigation-button onClick={handleAccountMenuClick}>
            <i className="fa-solid fa-user" /><span>Account Settings</span>
          </a>
          {user?.role === 'admin' && (
            <a href="/admin" className="account-dropdown-item" data-navigation-button onClick={handleAdminClick}>
              <i className="fa-solid fa-cog" /><span>Admin Panel</span>
            </a>
          )}
          <div className="account-dropdown-divider" />
          <div className="account-dropdown-item logout-item" onClick={handleLogout} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleLogout()}>
            <i className="fa-solid fa-right-from-bracket" /><span>Logout</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** Mobile row 2 right: credits only */
export function HeaderAccountPanelMobileCredits({ membershipDetails }) {
  const currentCredits = membershipDetails?.currentCredits ?? 0;
  const monthlyCredits = membershipDetails?.monthlyCredits ?? 0;
  const creditsDisplay = `${Number(currentCredits).toLocaleString()} / ${Number(monthlyCredits).toLocaleString()}`;
  return (
    <span className="header-account-mobile-credits" title="Credits remaining">
      {creditsDisplay}
    </span>
  );
}

export function HeaderAccountPanel({
  user,
  membershipDetails,
  isMaxPlan,
  isSupporter,
  isBetaTester,
  storedAccounts,
  onAccountMenuClick,
  onAdminClick,
  onLogout,
  onAccountSwitch,
  onRemoveAccount,
}) {
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAccountDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAccountButtonClick = () => {
    setAccountDropdownOpen(!accountDropdownOpen);
  };

  const handleAccountMenuClick = async () => {
    setAccountDropdownOpen(false);
    await onAccountMenuClick();
  };

  const handleAdminClick = async () => {
    setAccountDropdownOpen(false);
    await onAdminClick();
  };

  const handleLogout = async () => {
    setAccountDropdownOpen(false);
    await onLogout();
  };

  const handleAccountSwitch = async (accountId) => {
    setAccountDropdownOpen(false);
    try {
      const result = await onAccountSwitch(accountId);
      if (!result?.success) {
        console.error('Failed to switch account:', result?.error);
      }
    } catch (error) {
      console.error('Account switch error:', error);
    }
  };

  const currentCredits = membershipDetails?.currentCredits ?? 0;
  const monthlyCredits = membershipDetails?.monthlyCredits ?? 0;
  const creditsDisplay = `${Number(currentCredits).toLocaleString()} / ${Number(monthlyCredits).toLocaleString()}`;

  return (
    <div className="header-account-panel" ref={dropdownRef}>
      <div className="header-account-panel__inner">
        {/* Credits - x / y format */}
        <span className="header-account-panel__credits" title="Credits remaining">
          {creditsDisplay}
        </span>

        {/* Right group: badges + avatar, aligned right on mobile */}
        <div className="header-account-panel__right">
          {/* Supporter badge - small circle with tooltip */}
          {isSupporter && (
            <HeaderPanelBadge
              icon="fa-solid fa-check"
              className="header-account-panel__badge header-account-panel__badge--supporter"
              tooltipTitle="Supporter"
              tooltipText={SUPPORTER_TOOLTIP_TEXT}
            />
          )}

          {/* Max badge - small circle with tooltip */}
          {isMaxPlan && (
            <HeaderPanelBadge
              icon="fa-solid fa-crown"
              className="header-account-panel__badge header-account-panel__badge--max"
              tooltipTitle="Max User"
              tooltipText={MAX_TOOLTIP_TEXT}
            />
          )}

          {/* Beta tester badge - small circle with tooltip */}
          {isBetaTester && (
            <HeaderPanelBadge
              icon="fa-solid fa-flask"
              className="header-account-panel__badge header-account-panel__badge--beta"
              tooltipTitle="Beta Tester"
              tooltipText={BETA_TOOLTIP_TEXT}
            />
          )}

          {/* Avatar - flush right, triggers dropdown */}
          <div
          className="header-account-panel__avatar"
          title="Account Menu"
          onClick={handleAccountButtonClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleAccountButtonClick()}
          aria-haspopup="menu"
          aria-expanded={accountDropdownOpen}
        >
          {user?.initials ? (
            <span className="user-initial">{user.initials}</span>
          ) : (
            <i className="fa-solid fa-user" />
          )}
        </div>
        </div>
      </div>

      {accountDropdownOpen && (
        <div className="account-dropdown header-account-panel__dropdown">
          {storedAccounts
            .sort((a, b) => {
              const aIsCurrent = user?.email === a.email;
              const bIsCurrent = user?.email === b.email;
              if (aIsCurrent && !bIsCurrent) return -1;
              if (!aIsCurrent && bIsCurrent) return 1;
              return 0;
            })
            .map((account) => {
              const isCurrentAccount = user?.email === account.email;
              return (
                <a
                  key={account.id}
                  href={`/auth?switchToAccount=${account.id}&redirectTo=${encodeURIComponent(typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/')}`}
                  className={`account-dropdown-item account-full-item ${isCurrentAccount ? 'current' : ''}`}
                  data-navigation-button
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isCurrentAccount) handleAccountSwitch(account.id);
                  }}
                >
                  <div className="account-full-info">
                    <div className="account-avatar">
                      {account.userData?.initials || account.displayName?.charAt(0)?.toUpperCase() || account.email?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="account-details">
                      <div className="account-name">{account.displayName}</div>
                      <div className="account-email">{account.email}</div>
                    </div>
                  </div>
                  <div className="account-actions">
                    {isCurrentAccount && <i className="fa-solid fa-check account-check" />}
                    {!isCurrentAccount && (
                      <button
                        type="button"
                        className="account-remove-btn"
                        onClick={(e) => onRemoveAccount(account.id, e)}
                        title="Remove account"
                      >
                        <i className="fa-solid fa-times" />
                      </button>
                    )}
                  </div>
                </a>
              );
            })}

          <div className="account-dropdown-divider" />

          <a href="/account" className="account-dropdown-item" data-navigation-button onClick={handleAccountMenuClick}>
            <i className="fa-solid fa-user" />
            <span>Account Settings</span>
          </a>

          {user?.role === 'admin' && (
            <a href="/admin" className="account-dropdown-item" data-navigation-button onClick={handleAdminClick}>
              <i className="fa-solid fa-cog" />
              <span>Admin Panel</span>
            </a>
          )}

          <div className="account-dropdown-divider" />

          <div className="account-dropdown-item logout-item" onClick={handleLogout} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleLogout()}>
            <i className="fa-solid fa-right-from-bracket" />
            <span>Logout</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Small badge with RevoltTooltip (Supporter or Max)
function HeaderPanelBadge({ icon, className, tooltipTitle, tooltipText }) {
  const badgeRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const leaveTimeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    leaveTimeoutRef.current = setTimeout(() => setIsOpen(false), 100);
  };

  return (
    <span
      ref={badgeRef}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      tabIndex={0}
      aria-label={tooltipTitle}
    >
      <i className={icon} />
      <RevoltTooltip isOpen={isOpen} anchorRef={badgeRef} placement="bottom" maxWidth={320} interactive>
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <div className="revolt-tooltip-title">{tooltipTitle}</div>
          <div className="revolt-tooltip-body">{tooltipText}</div>
        </div>
      </RevoltTooltip>
    </span>
  );
}
